import { AnimatePresence, motion } from "framer-motion";
import { observer } from "mobx-react-lite";
import { useRef } from "react";

import { VariableSet } from "../../Playground/types";
import { useConfiguratorStore } from "../input-variable-configurator-store-context";
import { Variable } from "../types";

import ConfiguratorHeader from "./ConfiguratorHeader";
import VariableControls from "./VariableControls";
import VariableList from "./VariableList";

import EmptyPlaceholder from "@/components/ui/EmptyPlaceholder";
import useAutoScroll from "@/hooks/useAutoScroll";
import { RequestToolData } from "@/queries/requests/useRequestLogInputVariables";

interface ConfiguratorContentProps {
  onClose: () => void;
  isLoading?: boolean;
  useSaveButton?: boolean;
  hideKey?: boolean;
  hideImportButton?: boolean;
  hideExportButton?: boolean;
  placeholder?: string;
  enableAIDataset?: boolean;
  promptTemplate?: string;
  onSetNameImported?: (name: string) => void;
  onRequestImported?: (
    requestId: number,
    variables: Record<string, string>,
    toolData?: RequestToolData | null,
  ) => void;
  normalizeImportedVariables?: (variables: Record<string, string>) => Record<string, string>;
  activeSetName?: string;
  onAddVariableSet?: (variableSet: VariableSet) => void;
  currentVariableSetCount?: number;
  popoverPortalContainer?: HTMLElement | null;
}

const ConfiguratorContent = observer(
  ({
    onClose,
    isLoading,
    useSaveButton = false,
    hideKey = false,
    hideImportButton = false,
    hideExportButton = false,
    placeholder = "No input variables defined",
    enableAIDataset = false,
    promptTemplate = "",
    onSetNameImported,
    onRequestImported,
    normalizeImportedVariables,
    activeSetName,
    onAddVariableSet,
    currentVariableSetCount = 0,
    popoverPortalContainer,
  }: ConfiguratorContentProps) => {
    const store = useConfiguratorStore();
    const containerRef = useRef<HTMLDivElement>(null);

    const { lastItemRef } = useAutoScroll(store.filteredVariables, containerRef);

    const handleUpdateVariable = (variable: Variable) => {
      store.updateVariable(variable.id, variable.key, variable.value);
    };

    const handleRemoveVariable = (filteredIndex: number) => {
      const variableToRemove = store.filteredVariables[filteredIndex];
      store.removeVariable(variableToRemove.id);
    };

    return (
      <div className="flex h-full flex-col">
        {/* Header */}
        <div className="flex-shrink-0 px-4">
          <ConfiguratorHeader
            hideImportButton={hideImportButton}
            hideExportButton={hideExportButton}
            enableAIDataset={enableAIDataset}
            promptTemplate={promptTemplate}
            onSetNameImported={onSetNameImported}
            onRequestImported={onRequestImported}
            normalizeImportedVariables={normalizeImportedVariables}
            searchQuery={store.searchQuery}
            onSearchChange={value => store.setSearchQuery(value)}
            showSearch={store.variableCount > 0}
            activeSetName={activeSetName}
            onAddVariableSet={onAddVariableSet}
            currentVariableSetCount={currentVariableSetCount}
          />
          {store.isSearching && store.filteredVariableCount !== store.variableCount && (
            <div className="mt-2 rounded-md bg-gray-50 px-3 py-2 text-xs text-gray-600">
              Showing {store.filteredVariableCount} of {store.variableCount} variables
            </div>
          )}
        </div>

        {/* Content Area */}
        <div className="flex-grow overflow-hidden">
          <AnimatePresence mode="wait" initial={false}>
            <motion.div
              className="h-full w-full overflow-y-auto px-4 pb-2 pt-2"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              ref={containerRef}
              exit={{ opacity: 0, y: -20 }}
              transition={{
                duration: 0.15,
                ease: "easeOut",
              }}
            >
              <AnimatePresence mode="wait" initial={false}>
                {!store.hasSearchResults ? (
                  <motion.div
                    key="empty"
                    initial={{ opacity: 0, scale: 0.95 }}
                    animate={{ opacity: 1, scale: 1 }}
                    className="h-full"
                    exit={{ opacity: 0, scale: 0.95 }}
                    transition={{ duration: 0.15, ease: "easeOut" }}
                  >
                    <EmptyPlaceholder
                      className="h-full"
                      message={store.isSearching ? "No variables match your search" : placeholder}
                      icon
                    />
                  </motion.div>
                ) : (
                  <VariableList
                    variables={store.filteredVariables}
                    updateInputVariable={handleUpdateVariable}
                    removeInputVariable={handleRemoveVariable}
                    lastItemRef={lastItemRef}
                    hideKey={hideKey}
                    scrollContainerRef={containerRef}
                    popoverPortalContainer={popoverPortalContainer}
                  />
                )}
              </AnimatePresence>
            </motion.div>
          </AnimatePresence>
        </div>

        {/* Footer */}
        <div className="flex-shrink-0 border-t border-gray-200 px-4">
          <VariableControls
            useClearAll={store.variableCount > 0}
            useSaveButton={useSaveButton}
            clearAll={() => store.clearAll()}
            addInputVariable={() => store.addVariable()}
            onClose={onClose}
            isLoading={isLoading}
          />
        </div>
      </div>
    );
  },
);

ConfiguratorContent.displayName = "CurrentVariablesTab";

export default ConfiguratorContent;
